var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { Button, CircularFrame, Icon, InteractiveDiv, SearchInput, ContentFrame, LayoutContent, ScreenContainer, Typography, } from '@pinwheel/origami';
import { useSelector } from 'react-redux';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useOpts } from '../../context';
import usePageTracking from '../../hooks/usePageTracking';
import ModalHeader from '../../components/ModalHeader';
import { SearchResults } from './SearchResults';
import { EventAction } from '../../types/events';
import { SearchResponseType } from '../../types/types';
import { escapeRegex, getHubEnvironment } from '../../utils';
import useLinkNavigate from '../../hooks/useLinkNavigate';
import MessageDetail from '../../LinkApiRouter/views/SearchMain/MessageDetail';
import { DocumentUploadsExperience } from '../../types/document-uploads';
import { useGetLocalizedString } from '../../hooks/useGetLocalizedString';
import { selectDocumentUploads } from '../../state/slices/slice-document-uploads';
import { NUM_PLATFORMS_IN_VIEWPORT } from '../../LinkApiRouter/views/SearchMain/constants';
import { selectShouldShowDDFormsFallback } from '../../state/slices/slice-dd-form';
import { useDDFormContext } from '../../hooks/useDDFormContext';
import { checkHackyWorkspace } from '../../utils/hacky-workspace-customizations';
export var WORKDAY_ID_PROD = '5965580e-380f-4b86-8a8a-7278c77f73cb';
export var WORKDAY_ID_STAG = '715f8d94-f35d-4f52-be59-ae5886f001e9';
var FilterSearch = function (_a) {
    var loadResults = _a.loadResults, title = _a.title, eventSurface = _a.eventSurface, isWorkday = _a.isWorkday;
    var getLocalizedString = useGetLocalizedString();
    var opts = useOpts();
    var platformType = opts.linkTokenPayload.platformType, setSelectedPlatform = opts.setSelectedPlatform, setSelectedEmployer = opts.setSelectedEmployer, fireProperEventType = opts.fireProperEventType, fireClientEvent = opts.fireClientEvent, featureFlags = opts.featureFlags;
    var documentUploads = useSelector(selectDocumentUploads);
    var shouldShowDDForms = useSelector(selectShouldShowDDFormsFallback);
    var navigate = useLinkNavigate();
    var _b = useState(true), isLoading = _b[0], setLoading = _b[1];
    var _c = useState(''), searchText = _c[0], setSearchText = _c[1];
    var _d = useState([]), allResults = _d[0], setAllResults = _d[1];
    var setDDFormEntryPoint = useDDFormContext().setDDFormEntryPoint;
    // We currently have 26 top platforms specified so going with this for an
    // initial value to start with.
    var NUM_TOP_RESULTS = 26;
    // For workday, we currently have 32 prioritized employers
    // const NUM_TOP_WORKDAY_RESULTS = 32;
    var fireImpressionEvent = usePageTracking(eventSurface).fireImpressionEvent;
    var linkTokenPayload = useOpts().linkTokenPayload;
    var workspaceName = linkTokenPayload === null || linkTokenPayload === void 0 ? void 0 : linkTokenPayload.workspaceName;
    var isCitadel = checkHackyWorkspace('Citadel CU', workspaceName) ||
        checkHackyWorkspace('Citadel Theme Testing', workspaceName);
    var isFlow = checkHackyWorkspace('Flow', workspaceName) ||
        checkHackyWorkspace('Flow Theme Testing', workspaceName);
    var filteredResults = useMemo(function () {
        if (!searchText) {
            return platformType === 'time_and_attendance'
                ? allResults
                : allResults.slice(0, NUM_TOP_RESULTS);
        }
        var regex = new RegExp("(^|\\W)".concat(escapeRegex(searchText)), 'i');
        return allResults.filter(function (r) { return regex.test(r.name); });
    }, [searchText, allResults, platformType]);
    var viewportResults = useMemo(function () { return filteredResults.slice(0, NUM_PLATFORMS_IN_VIEWPORT); }, [filteredResults]);
    useEffect(function () {
        fireImpressionEvent({ searchText: searchText, viewportResults: viewportResults });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchText, viewportResults]);
    useEffect(function () {
        setSearchText('');
        setLoading(true);
        loadResults()
            .then(function (results) {
            setAllResults(results);
            setLoading(false);
        })
            .catch(function () { return setLoading(false); });
    }, [loadResults]);
    var onResultClick = useCallback(function (result) {
        if (result.responseType === SearchResponseType.PLATFORM) {
            setSelectedEmployer(null);
            setSelectedPlatform(result);
            fireClientEvent('select_platform', {
                selectedPlatformId: result.id,
                selectedPlatformName: result.name,
            });
        }
        else {
            setSelectedPlatform(null);
            setSelectedEmployer(result);
            fireClientEvent('select_employer', {
                selectedEmployerId: result.id,
                selectedEmployerName: result.name,
            });
        }
        fireProperEventType(EventAction.EMPLOYER_OR_PLATFORM_SELECTED, opts, {
            searchText: searchText,
            viewportResults: viewportResults,
            platformIdOverride: result.id,
        });
        navigate('/link-api/login');
    }, [
        fireProperEventType,
        opts,
        searchText,
        viewportResults,
        navigate,
        setSelectedPlatform,
        fireClientEvent,
        setSelectedEmployer,
    ]);
    var cantFindEmployerPrompt = useMemo(function () {
        var id = 'searchResults-platformSearch';
        return (React.createElement(InteractiveDiv, { "data-testid": id, "aria-labelledby": id, onClick: function () {
                return onResultClick({
                    id: getHubEnvironment() === 'production'
                        ? WORKDAY_ID_PROD
                        : WORKDAY_ID_STAG,
                    name: '',
                    logoUrl: '',
                    responseType: SearchResponseType.PLATFORM,
                });
            }, sx: {
                cursor: 'pointer',
                fontWeight: '500',
                position: 'relative',
                padding: '12px',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                wordBreak: 'break-word',
                '&:hover': {
                    backgroundColor: {
                        type: 'theme',
                        colorLegacy: 'Neutral/10',
                        colorNext: 'neutral/default',
                        opacityNext: 0.12,
                    },
                },
                '&:active': {
                    backgroundColor: {
                        type: 'theme',
                        colorLegacy: 'Neutral/20',
                        colorNext: 'neutral/default',
                        opacityNext: 0.24,
                    },
                },
            } },
            React.createElement(CircularFrame, { backgroundColor: {
                    type: 'theme',
                    colorLegacy: 'Neutral/10',
                    colorNext: 'background/alt',
                } },
                React.createElement(Icon, { icon: "building", color: {
                        type: 'theme',
                        colorLegacy: 'Neutral/70',
                        colorNext: 'neutral/default',
                    } })),
            React.createElement(Typography, { id: id, sx: { marginLeft: '12px' } }, getLocalizedString('CANT_FIND_EMPLOYER_WORKDAY'))));
    }, [getLocalizedString, onResultClick]);
    var ddFormFallbackPrompt = useMemo(function () { return (React.createElement(React.Fragment, null,
        React.createElement(MessageDetail, { message: getLocalizedString('NO_RESULTS_NO_PROBLEM'), detail: getLocalizedString('YOU_CAN_GENERATE_A_FORM') }),
        React.createElement(Button, __assign({ variant: "outline" }, (isCitadel ? { color: 'secondary' } : {}), { "data-testid": "search-useDDForm", onClick: function () {
                setDDFormEntryPoint('search');
                navigate('/dd-form/primer');
            } }), getLocalizedString('USE_A_FORM_INSTEAD')))); }, [getLocalizedString, navigate, setDDFormEntryPoint, isCitadel]);
    var noProviderResultsPrompt = useMemo(function () { return (React.createElement(MessageDetail, { message: getLocalizedString('CANT_FIND_RESULT'), detail: getLocalizedString('CANT_FIND_PAYROLL_PLATFORM_TIP') })); }, [getLocalizedString]);
    var uploadDocumentsInsteadButton = useMemo(function () { return (React.createElement(Button, __assign({ variant: "outline" }, (isFlow ? { color: 'secondary' } : {}), { "data-testid": "DocumentUploads-upload-documents-instead-button", onClick: function () {
            navigate('/document-uploads/primer');
        } }), getLocalizedString('UPLOAD_DOCUMENT_INSTEAD'))); }, [getLocalizedString, navigate, isFlow]);
    var searchResultsLastItem = useMemo(function () {
        if (isLoading) {
            return null;
        }
        if (isWorkday) {
            return cantFindEmployerPrompt;
        }
        if ((featureFlags === null || featureFlags === void 0 ? void 0 : featureFlags.documentUploadsEnabled) &&
            documentUploads === DocumentUploadsExperience.FALLBACK) {
            return (React.createElement(React.Fragment, null,
                noProviderResultsPrompt,
                uploadDocumentsInsteadButton));
        }
        if (shouldShowDDForms) {
            return ddFormFallbackPrompt;
        }
        return noProviderResultsPrompt;
    }, [
        cantFindEmployerPrompt,
        ddFormFallbackPrompt,
        featureFlags === null || featureFlags === void 0 ? void 0 : featureFlags.documentUploadsEnabled,
        isLoading,
        isWorkday,
        noProviderResultsPrompt,
        uploadDocumentsInsteadButton,
        documentUploads,
        shouldShowDDForms,
    ]);
    return (React.createElement(ScreenContainer, { "data-testid": "FilterSearch" },
        React.createElement(ModalHeader, { title: title }),
        React.createElement(SearchInput, { sx: {
                padding: '0 24px 0 24px',
                boxSizing: 'border-box',
            }, value: searchText, onChange: function (value) { return setSearchText(value); }, placeholder: getLocalizedString('SEARCH_BY_NAME'), styleInputContainer: { margin: 0 } }),
        React.createElement(ContentFrame, null,
            React.createElement(LayoutContent, null,
                React.createElement(SearchResults, { results: filteredResults, onResultClick: onResultClick, loading: isLoading, lastItem: searchResultsLastItem })))));
};
export default FilterSearch;
